/**
 * The pure half of the AI answering tab: which lines the card says about
 * itself, and what its form is allowed to send.
 *
 * Its own React-free file for the reason `telephonyChecklist` is one: the part
 * worth testing is not the rendering, it is deciding how loudly to say each
 * gap, and making sure one mistyped field cannot sink the whole save. The PUT
 * schema checks BEFORE the server's clamp runs, and it refuses the entire
 * request over more than 20 test numbers, an entry longer than 40 characters
 * or a timing that is not a whole number. So the form reads its own inputs
 * through the functions below, and says what it dropped, rather than handing
 * the server a request it will 400.
 */
import { normalizeE164 } from "@wa/core/phone";
import {
  AGENT_BLOCKERS,
  AGENT_CHARS_PER_TOKEN,
  AGENT_MAX_TEST_CALLERS,
  type AgentBlocker,
} from "../../../../../lib/telephonyAgent";
import type { TtsVoice } from "../../../../../lib/telephonyNotice";

// ---------------------------------------------------------------- blockers

export interface AgentNote {
  /** The server's code, verbatim. An unknown one is printed in its note. */
  blocker: string;
  tone: "bad" | "warn";
  /** Suffix under `settings.aiAgent.blocker`: the code itself, or "unknown". */
  key: string;
}

/**
 * The two gaps that are a decision still open on this very page rather than
 * something missing: the switch, and whom the AI should answer. A remark, not
 * a fault. Everything else is a piece the AI cannot work without - a key, a
 * voice, the address Twilio streams to, or the greeting that carries the legal
 * disclosure - and reads as one.
 */
const OPEN_DECISIONS: ReadonlySet<string> = new Set<AgentBlocker>(["disabled", "no_mode"]);
const KNOWN: ReadonlySet<string> = new Set<string>(AGENT_BLOCKERS);

/**
 * One note per blocker, in the server's order (the order somebody would fix
 * them in).
 *
 * A code this build does not know still gets a line. A newer server naming a
 * gap the page cannot word must not look exactly like a server naming none:
 * the switch would then read as blocked by nothing.
 */
export function agentBlockerNotes(blockers: readonly string[] | undefined): AgentNote[] {
  if (!blockers) return [];
  const seen = new Set<string>();
  const notes: AgentNote[] = [];
  for (const blocker of blockers) {
    if (seen.has(blocker)) continue;
    seen.add(blocker);
    notes.push({
      blocker,
      tone: OPEN_DECISIONS.has(blocker) ? "warn" : "bad",
      key: KNOWN.has(blocker) ? blocker : "unknown",
    });
  }
  return notes;
}

// ---------------------------------------------------------------- test numbers

/** The PUT schema's ceiling on one entry (`z.string().max(40)`). */
const CALLER_MAX_CHARS = 40;

export interface TestCallersRead {
  /** What the form sends: E.164, each number once, at most the server's ceiling. */
  numbers: string[];
  /** Lines that are not a phone number. The server would drop them without a word. */
  invalid: string[];
  /** Real numbers past the ceiling. Sending them would 400 the whole save. */
  overflow: string[];
}

/**
 * The test-number box as the list the server will keep.
 *
 * One per line is what the hint asks for, but a comma-separated paste is split
 * too - it is the same list, and refusing it would only mean retyping. Numbers
 * go through the same `normalizeE164` the server applies (both import it from
 * `@wa/core/phone`), so "5551 2345" and "+372 5551 2345" are one number here
 * exactly as they are one number to the call that has to match it.
 *
 * `max` is the ceiling of whichever schema the list is sent to: the answering
 * AI's test callers by default, the outbound tab passes its own (five).
 */
export function readTestCallers(text: string, max: number = AGENT_MAX_TEST_CALLERS): TestCallersRead {
  const numbers: string[] = [];
  const invalid: string[] = [];
  const overflow: string[] = [];
  const seen = new Set<string>();
  for (const raw of text.split(/[\n,;]+/)) {
    const line = raw.trim();
    if (!line) continue;
    const e164 = line.length <= CALLER_MAX_CHARS ? normalizeE164(line) : null;
    if (!e164) {
      invalid.push(line);
      continue;
    }
    if (seen.has(e164)) continue;
    seen.add(e164);
    if (numbers.length < max) numbers.push(e164);
    else overflow.push(e164);
  }
  return { numbers, invalid, overflow };
}

// ---------------------------------------------------------------- numbers

export interface IntBounds {
  min: number;
  max: number;
  fallback: number;
}

/**
 * A number box's text as the whole number the server will keep.
 *
 * Clamped here as well as on the server because the schema looks first: an
 * empty box coerces to 0 and fails its `min(1)`, a decimal fails `int()`, and
 * either refuses the whole save over a field the clamp would have fixed. Empty
 * or unreadable means the default; a space as a thousands separator and a
 * decimal comma are read the way an Estonian keyboard types them.
 */
export function readBoundedInt(text: string, bounds: IntBounds): number {
  const cleaned = text.replace(/\s+/g, "").replace(",", ".");
  const n = cleaned ? Number(cleaned) : Number.NaN;
  if (!Number.isFinite(n)) return bounds.fallback;
  return Math.min(bounds.max, Math.max(bounds.min, Math.round(n)));
}

// ---------------------------------------------------------------- voice

export interface VoiceOption {
  value: string;
  label: string;
  /** The saved value, shown although the list does not hold it. */
  saved?: boolean;
}

/**
 * The voice menu: every ElevenLabs voice the workspace lists, plus the saved
 * value when the list does not have it.
 *
 * Only ElevenLabs, because it is the only service that can stream speech into
 * a live call (the server reads anything else as "no voice"). The extra row is
 * the other half: a voice deleted from the workspace, a bare id typed by hand
 * or a list that failed to load would otherwise leave the menu blank, and a
 * blank menu reads as "nothing chosen" - a blocker the server is not reporting.
 */
export function agentVoiceOptions(
  value: string,
  voices: readonly TtsVoice[],
): { options: VoiceOption[]; selected: string } {
  const options: VoiceOption[] = voices
    .filter((v) => v.provider === "elevenlabs")
    .map((v) => ({ value: v.id, label: v.label }));
  const current = value.trim();
  if (!current) return { options, selected: "" };
  // A bare id and its `elevenlabs:` reference are the same voice. The server
  // accepts both, and the menu must not show a saved voice as missing.
  const match = options.find((o) => o.value === current || o.value === `elevenlabs:${current}`);
  if (match) return { options, selected: match.value };
  return { options: [{ value: current, label: current, saved: true }, ...options], selected: current };
}

/**
 * The id under a voice's name, for the second line of a menu row.
 *
 * A workspace can hold two voices called the same thing, and the id is what the
 * call actually runs on - so the row shows both. The service prefix is dropped
 * because every voice in this menu is an ElevenLabs one and repeating that on
 * every row says nothing. Returns "" when the label already IS the id (the
 * saved-but-unlisted row), where a second line would be the same string twice.
 */
export function voiceIdHint(option: VoiceOption): string {
  const id = option.value.includes(":") ? option.value.slice(option.value.indexOf(":") + 1) : option.value;
  return id === option.label ? "" : id;
}

// ---------------------------------------------------------------- knowledge

/**
 * Tokens for the text being edited, by the server's own formula (`approxTokens`
 * in `knowledge.ts`), so the count for unsaved text is the number the server
 * reports once it is saved. The test beside this file holds the constant
 * against the server's.
 */
export function estimateTokens(chars: number): number {
  return chars > 0 ? Math.ceil(chars / AGENT_CHARS_PER_TOKEN) : 0;
}
